import React from 'react';
import DodTermText from '../DodTermText';
import './Connect2AiPanel.css';

type ScopeRow = { id: string; label: string };
type KeyRow = {
  key_id: string;
  label: string;
  prefix: string;
  scopes: string[];
  created_at: string;
  revoked_at?: string;
  last_used_at?: string;
};
type SessionRow = {
  token_id: string;
  client_id: string;
  scopes: string[];
  created_at: string;
  revoked_at?: string;
  email?: string;
};

type StatusPayload = {
  mcp_url: string;
  readonly_url: string;
  auth_issuer: string;
  spec?: {
    consent?: { title?: string; lead?: string; what_ai_can?: string; what_ai_cannot?: string; cannot_body?: string };
    scopes?: ScopeRow[];
    tools?: Array<{ name: string; scopes: string[]; description: string }>;
  };
  snippets?: {
    claude_cli?: string;
    cursor?: unknown;
    generic_header_note?: string;
  };
  deep_links?: { cursor?: string; claude_cli?: string };
  keys?: KeyRow[];
  oauth_sessions?: SessionRow[];
};

type Props = {
  language: 'en' | 'es';
  gatewayBaseUrl: string;
  accessToken: string;
  readOnly?: boolean;
};

/**
 * Connect2AI — let a subscriber point Cursor / Claude / any MCP client at
 * a secure NextCompli node (remote MCP + OAuth + restricted AI keys).
 */
export default function Connect2AiPanel({
  language,
  gatewayBaseUrl,
  accessToken,
  readOnly = false,
}: Props): React.ReactElement {
  const gateway = gatewayBaseUrl.replace(/\/$/, '');
  const es = language === 'es';
  const [status, setStatus] = React.useState<StatusPayload | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [mintedKey, setMintedKey] = React.useState('');
  const [keyLabel, setKeyLabel] = React.useState('');
  const [wantWrite, setWantWrite] = React.useState(false);

  const copy = es
    ? {
      title: 'Connect2AI',
      lead: 'Conecte Cursor, Claude u otro cliente MCP a un nodo seguro de NextCompli. Lectura y borradores por defecto; la escritura exige confirmación.',
      urls: 'URLs del nodo',
      mcp: 'MCP (lectura + borrador + escritura según alcance)',
      readonly: 'Solo lectura',
      clients: 'Clientes',
      readyOAuth: 'Listo para OAuth en el navegador',
      readyDone: 'Navegador listo 15 min. Abra su cliente MCP y autorice.',
      mint: 'Crear clave de IA (ncai_)',
      mintWrite: 'Incluir escritura (aprobar/rechazar borradores)',
      copyUrl: 'Copiar URL',
      copyKey: 'Copiar clave',
      copyCli: 'Copiar comando Claude',
      openCursor: 'Abrir en Cursor',
      keys: 'Claves de IA',
      sessions: 'Sesiones OAuth',
      revoke: 'Revocar',
      none: 'Ninguna aún.',
      scopes: 'Alcances',
      tools: 'Herramientas',
      secretOnce: 'Copie la clave ahora. NextCompli solo guarda el hash. No es un token de instalación del agente (ncagent_).',
      loadError: 'No se pudo cargar Connect2AI.',
      saveError: 'No se pudo completar la acción.',
      labelPlaceholder: 'p. ej. Cursor de Stefany',
    }
    : {
      title: 'Connect2AI',
      lead: 'Point Cursor, Claude, or any MCP client at a secure NextCompli node. Read and draft by default; write requires confirmation.',
      urls: 'Node URLs',
      mcp: 'MCP (read + draft + write by scope)',
      readonly: 'Read-only',
      clients: 'Clients',
      readyOAuth: 'Ready for browser OAuth',
      readyDone: 'Browser ready for 15 minutes. Open your MCP client and authorize.',
      mint: 'Create AI key (ncai_)',
      mintWrite: 'Include write (approve/reject drafts)',
      copyUrl: 'Copy URL',
      copyKey: 'Copy key',
      copyCli: 'Copy Claude command',
      openCursor: 'Open in Cursor',
      keys: 'AI keys',
      sessions: 'OAuth sessions',
      revoke: 'Revoke',
      none: 'None yet.',
      scopes: 'Scopes',
      tools: 'Tools',
      secretOnce: 'Copy this key now. NextCompli stores only a hash. This is not an agent install token (ncagent_).',
      loadError: 'Could not load Connect2AI.',
      saveError: 'Could not complete that action.',
      labelPlaceholder: 'e.g. Stefany’s Cursor',
    };

  const load = React.useCallback(async () => {
    if (!accessToken || !gateway) return;
    setBusy(true);
    setError('');
    try {
      const response = await fetch(
        `${gateway}/api/v1/connect2ai/status?language=${encodeURIComponent(language)}`,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      );
      const body = await response.json() as StatusPayload & { detail?: string };
      if (!response.ok) {
        setError(typeof body.detail === 'string' ? body.detail : copy.loadError);
        return;
      }
      setStatus(body);
    } catch {
      setError(copy.loadError);
    } finally {
      setBusy(false);
    }
  }, [accessToken, copy.loadError, gateway, language]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const copyText = async (value: string, okMessage: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setMessage(okMessage);
    } catch {
      setError(copy.saveError);
    }
  };

  const readyOAuth = async () => {
    if (readOnly) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const response = await fetch(`${gateway}/api/v1/connect2ai/browser-bridge`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const body = await response.json() as { note?: string; detail?: string };
      if (!response.ok) {
        setError(typeof body.detail === 'string' ? body.detail : copy.saveError);
        return;
      }
      setMessage(copy.readyDone);
    } catch {
      setError(copy.saveError);
    } finally {
      setBusy(false);
    }
  };

  const mintKey = async () => {
    if (readOnly) return;
    setBusy(true);
    setError('');
    setMessage('');
    setMintedKey('');
    try {
      const scopes = wantWrite
        ? ['mcp:read', 'mcp:draft', 'mcp:write']
        : ['mcp:read', 'mcp:draft'];
      const response = await fetch(`${gateway}/api/v1/connect2ai/keys`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ label: keyLabel, scopes }),
      });
      const body = await response.json() as { token?: string; detail?: string };
      if (!response.ok || !body.token) {
        setError(typeof body.detail === 'string' ? body.detail : copy.saveError);
        return;
      }
      setMintedKey(body.token);
      setKeyLabel('');
      await load();
    } catch {
      setError(copy.saveError);
    } finally {
      setBusy(false);
    }
  };

  const revokeKey = async (keyId: string) => {
    if (readOnly) return;
    setBusy(true);
    setError('');
    try {
      const response = await fetch(
        `${gateway}/api/v1/connect2ai/keys/${encodeURIComponent(keyId)}/revoke`,
        { method: 'POST', headers: { Authorization: `Bearer ${accessToken}` } },
      );
      if (!response.ok) {
        setError(copy.saveError);
        return;
      }
      await load();
    } catch {
      setError(copy.saveError);
    } finally {
      setBusy(false);
    }
  };

  const revokeSession = async (tokenId: string) => {
    if (readOnly) return;
    setBusy(true);
    setError('');
    try {
      const response = await fetch(
        `${gateway}/api/v1/connect2ai/oauth/sessions/${encodeURIComponent(tokenId)}/revoke`,
        { method: 'POST', headers: { Authorization: `Bearer ${accessToken}` } },
      );
      if (!response.ok) {
        setError(copy.saveError);
        return;
      }
      await load();
    } catch {
      setError(copy.saveError);
    } finally {
      setBusy(false);
    }
  };

  const consent = status?.spec?.consent;
  const scopes = status?.spec?.scopes || [];
  const tools = status?.spec?.tools || [];
  const keys = (status?.keys || []).filter((row) => !row.revoked_at);
  const sessions = (status?.oauth_sessions || []).filter((row) => !row.revoked_at);
  const seenTerms = new Set<string>();

  return (
    <section className="connect2ai-panel" aria-labelledby="connect2ai-title">
      <div>
        <h2 id="connect2ai-title">{copy.title}</h2>
        <p className="connect2ai-panel__lead">
          <DodTermText text={consent?.lead || copy.lead} language={language} seen={seenTerms} />
        </p>
      </div>

      {error ? <p className="connect2ai-panel__error" role="alert">{error}</p> : null}
      {message ? <p className="connect2ai-panel__ok" role="status">{message}</p> : null}

      <div className="connect2ai-panel__grid">
        <div className="connect2ai-panel__card">
          <h3>{copy.urls}</h3>
          <p className="connect2ai-panel__help">{copy.mcp}</p>
          <code className="connect2ai-panel__mono">{status?.mcp_url || '—'}</code>
          <div className="connect2ai-panel__actions">
            <button
              type="button"
              className="dashboard-btn"
              disabled={!status?.mcp_url}
              onClick={() => void copyText(String(status?.mcp_url || ''), es ? 'URL copiada.' : 'URL copied.')}
            >
              {copy.copyUrl}
            </button>
          </div>
          <p className="connect2ai-panel__help">{copy.readonly}</p>
          <code className="connect2ai-panel__mono">{status?.readonly_url || '—'}</code>
          <div className="connect2ai-panel__actions">
            <button
              type="button"
              className="dashboard-btn"
              disabled={!status?.readonly_url}
              onClick={() => void copyText(String(status?.readonly_url || ''), es ? 'URL copiada.' : 'URL copied.')}
            >
              {copy.copyUrl}
            </button>
          </div>
        </div>

        <div className="connect2ai-panel__card">
          <h3>{copy.clients}</h3>
          <p className="connect2ai-panel__help">
            <DodTermText
              text={status?.snippets?.generic_header_note || ''}
              language={language}
              seen={new Set()}
            />
          </p>
          <div className="connect2ai-panel__actions">
            {!readOnly ? (
              <button type="button" className="dashboard-btn dashboard-btn-primary" disabled={busy} onClick={() => void readyOAuth()}>
                {copy.readyOAuth}
              </button>
            ) : null}
            {status?.deep_links?.cursor ? (
              <a className="dashboard-btn" href={status.deep_links.cursor}>
                {copy.openCursor}
              </a>
            ) : null}
            <button
              type="button"
              className="dashboard-btn"
              disabled={!status?.snippets?.claude_cli}
              onClick={() => void copyText(String(status?.snippets?.claude_cli || ''), es ? 'Comando copiado.' : 'Command copied.')}
            >
              {copy.copyCli}
            </button>
          </div>
        </div>

        <div className="connect2ai-panel__card">
          <h3>{copy.mint}</h3>
          {!readOnly ? (
            <>
              <label className="connect2ai-panel__help">
                <input
                  type="text"
                  value={keyLabel}
                  disabled={busy}
                  placeholder={copy.labelPlaceholder}
                  onChange={(event) => setKeyLabel(event.target.value)}
                />
              </label>
              <label className="connect2ai-panel__help">
                <input
                  type="checkbox"
                  checked={wantWrite}
                  disabled={busy}
                  onChange={(event) => setWantWrite(event.target.checked)}
                />
                {' '}
                {copy.mintWrite}
              </label>
              <button type="button" className="dashboard-btn dashboard-btn-primary" disabled={busy} onClick={() => void mintKey()}>
                {copy.mint}
              </button>
            </>
          ) : null}
          {mintedKey ? (
            <div className="connect2ai-panel__secret" role="status">
              <p className="connect2ai-panel__help">{copy.secretOnce}</p>
              <code className="connect2ai-panel__mono">{mintedKey}</code>
              <button type="button" className="dashboard-btn" onClick={() => void copyText(mintedKey, es ? 'Clave copiada.' : 'Key copied.')}>
                {copy.copyKey}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <div className="connect2ai-panel__grid">
        <div className="connect2ai-panel__card">
          <h3>{copy.scopes}</h3>
          <div className="connect2ai-panel__scopes">
            {scopes.map((scope) => (
              <span key={scope.id} className="connect2ai-panel__scope" title={scope.label}>
                {scope.id}
              </span>
            ))}
          </div>
          <ul className="connect2ai-panel__list">
            {scopes.map((scope) => (
              <li key={`desc-${scope.id}`}>
                <span>
                  <strong>{scope.id}</strong>
                  {' — '}
                  <DodTermText text={scope.label} language={language} seen={new Set()} />
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="connect2ai-panel__card">
          <h3>{copy.tools}</h3>
          <ul className="connect2ai-panel__list">
            {tools.map((tool) => (
              <li key={tool.name}>
                <span>
                  <strong>{tool.name}</strong>
                  {' · '}
                  {(tool.scopes || []).join(', ')}
                  <br />
                  <span className="connect2ai-panel__help">{tool.description}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="connect2ai-panel__grid">
        <div className="connect2ai-panel__card">
          <h3>{copy.keys}</h3>
          {keys.length ? (
            <ul className="connect2ai-panel__list">
              {keys.map((row) => (
                <li key={row.key_id}>
                  <span>
                    <strong>{row.label || row.key_id}</strong>
                    {' · '}
                    {row.prefix}…
                    <br />
                    <span className="connect2ai-panel__help">{(row.scopes || []).join(', ')}</span>
                  </span>
                  {!readOnly ? (
                    <button type="button" className="dashboard-btn" disabled={busy} onClick={() => void revokeKey(row.key_id)}>
                      {copy.revoke}
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="connect2ai-panel__help">{copy.none}</p>
          )}
        </div>

        <div className="connect2ai-panel__card">
          <h3>{copy.sessions}</h3>
          {sessions.length ? (
            <ul className="connect2ai-panel__list">
              {sessions.map((row) => (
                <li key={row.token_id}>
                  <span>
                    <strong>{row.client_id}</strong>
                    {row.email ? ` · ${row.email}` : ''}
                    <br />
                    <span className="connect2ai-panel__help">{(row.scopes || []).join(', ')}</span>
                  </span>
                  {!readOnly ? (
                    <button type="button" className="dashboard-btn" disabled={busy} onClick={() => void revokeSession(row.token_id)}>
                      {copy.revoke}
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="connect2ai-panel__help">{copy.none}</p>
          )}
        </div>
      </div>
    </section>
  );
}
